/**
 * Con qué entrega Smarty Drop hoy (20260925211000).
 *
 * El founder (26-sep): «en la opción de venta deja habilitado solo la opción de trabajar en provincia
 * con Shalom y Olva. El courier de Lima aún no lo tengo, así que no debes mostrarlo aún. Hablo solo de
 * los que son Smarty Drop: si es un plan pago y tiene producto propio, puede usar su courier y sus
 * integraciones».
 *
 * ⭐ LA BASE LO DICE (`fn_drop_transportistas`): lo que Smarty activó en su Transporte. La pantalla, la
 *    venta nueva y la puerta al almacén leen lo mismo; el día que Smarty active un courier de Lima, el
 *    motorizado aparece solo, sin tocar código.
 *
 * 🚨 ANTES, «Agregar un envío» de una venta de drop ofrecía el catálogo general —Dropi, InDrive,
 *    Reparto propio—: couriers que no trabajan con Smarty Drop y que el almacén no puede usar.
 */
import { supabase } from '@/lib/supabase';
import { opcionesDeCourierParaElPedido, porQueNoCubre } from '@/lib/dropshipping/couriersDelDrop';
import { laRedComoOpciones, opcionesDeLaRed } from '@/lib/logistics/laRedDeSmarty';
import { MARCA_DE_ZONA_ROJA } from '@/lib/logistics/laZonaRojaDelCourier';
import {
  resolveCouriersForDestination, type AvailableCourierOption, type LogisticsDestination, type ResolveCouriersResult,
} from '@/lib/logistics-resolver';

/** Cómo llega el pedido: a una agencia (Shalom u Olva) o a la puerta con un motorizado. */
export type ModalidadDeEntrega = 'agencia' | 'motorizado';

export interface TransportistaDeSmartyDrop {
  courier_id: string;
  nombre: string;
  modalidad: ModalidadDeEntrega;
  logo_url: string | null;
  brand_color: string | null;
}

export async function transportistasDeSmartyDrop(): Promise<TransportistaDeSmartyDrop[]> {
  const { data, error } = await supabase.rpc('fn_drop_transportistas');
  if (error) throw error;
  return (data ?? []) as TransportistaDeSmartyDrop[];
}

/**
 * Cómo nace una venta nueva. `null`: no va por Smarty Drop, y el negocio trabaja con sus couriers como
 * siempre. Va por Smarty Drop si el plan es de Drop o si el producto es de un proveedor de Drop.
 */
export async function modalidadDeLaVentaNueva(tenantId: string, productoId: string | null): Promise<ModalidadDeEntrega | null> {
  const { data, error } = await supabase.rpc('fn_drop_modalidad_de_la_venta_nueva', { p_tenant: tenantId, p_producto: productoId });
  if (error) throw error;
  return (data ?? null) as ModalidadDeEntrega | null;
}

/**
 * Si la venta va por Smarty Drop: el plan es de Drop, la venta tiene productos de Drop (existe su plan
 * de cumplimiento) o es el encargo que despacha un almacén.
 *
 * 🪤 SU PROPIO ALMACÉN NO LA VUELVE DE DROP (20261028150000): la venta propia que sale por la red de Smarty lleva como
 *    almacén al negocio mismo (ahí recoge el courier). Es la misma regla de la base (`fn_drop_la_venta_va_por_smarty_drop`:
 *    un almacén DISTINTO de quien vende). El ENCARGO sí es de Drop aunque el almacén sea su dueño (Vitalex provee y
 *    despacha): lo dice su canal.
 */
export function laVentaVaPorSmartyDrop(v: {
  planDeDrop: boolean; tieneCumplimiento: boolean; almacenTenantId?: string | null; tenantId?: string | null; canal?: string | null;
}): boolean {
  return v.planDeDrop || v.tieneCumplimiento || v.canal === 'smartydrop'
    || (!!v.almacenTenantId && (v.tenantId === undefined || v.almacenTenantId !== v.tenantId));
}

/**
 * Las agencias de Smarty Drop como opciones del selector de envío.
 *
 * 🪤 SIN COBERTURA DEL NEGOCIO: la red de la agencia es nacional y el almacén despacha con la cuenta de
 *    Smarty. Un drop no tiene —ni tiene por qué tener— catálogo de logística propio.
 * 🪤 SIN RECOMENDADA: Shalom u Olva cambian quién despacha y cuánto cuesta. Se elige a conciencia.
 */
export function agenciasComoOpciones(ts: TransportistaDeSmartyDrop[]): ResolveCouriersResult {
  const options: AvailableCourierOption[] = ts.filter((t) => t.modalidad === 'agencia').map((t, i) => ({
    courier_service_id: `smarty-drop:${t.courier_id}`,
    courier_id: t.courier_id,
    courier_name: t.nombre,
    courier_type: 'agency',
    service_name: 'Recojo en agencia',
    delivery_type: 'agency',
    priority: i,
    is_auto_assign: false,
    logo_url: t.logo_url,
    brand_color: t.brand_color,
  }));
  return { options, recommended_service_id: null };
}

/** El prefijo de las opciones que son couriers de Smarty Drop: al elegirlas se asignan, no se escriben. */
export const OPCION_DE_COURIER_DE_DROP = 'smarty-drop-courier:';

/**
 * Los couriers de Smarty Drop para ESTA venta (GSG, los motorizados de Lima), con su tarifa final y su zona,
 * como opciones del selector de envío. Founder, 30-sep: «al courier se le agrega en la lista de envíos:
 * tanto agencias como couriers como GSG» — la columna Destino de Ventas queda como atajo.
 *
 * 🪤 UNA VENTA POR AGENCIA NO PUEDE LLEVARLOS: la base sólo asigna un courier a una venta a domicilio
 *    (`is_cod`), y no hay conversión de agencia a motorizado. Salen deshabilitados, con el motivo.
 */
export async function couriersComoOpciones(ventaId: string, motorizado: boolean): Promise<AvailableCourierOption[]> {
  const opciones = await opcionesDeCourierParaElPedido(ventaId);
  return opciones.map((o, i) => ({
    courier_service_id: `${OPCION_DE_COURIER_DE_DROP}${o.courier_id}`,
    courier_id: o.courier_id,
    courier_name: o.courier,
    courier_type: 'motorizado',
    service_name: !motorizado ? 'A domicilio · esta venta va por agencia'
      : !o.cubre ? `A domicilio · ${porQueNoCubre(o).toLowerCase()}`
      : `A domicilio · S/ ${o.tarifa.toFixed(2)}${o.zona ? ` · zona ${o.zona}` : ''}${o.coordinar ? ` · ${MARCA_DE_ZONA_ROJA}` : ''}`,
    delivery_type: 'home',
    priority: 100 + i,
    is_auto_assign: false,
    logo_url: null,
    brand_color: null,
    disabled: !motorizado || !o.cubre,
    // ⭐ 20261029010000: la zona roja del courier (se elige, pero entrega coordinando).
    coordinar: o.cubre ? o.coordinar ?? null : null,
  }));
}

/**
 * Los transportistas que puede usar una venta: los de Smarty Drop si va por ahí (sus agencias y, si se
 * pasa la venta, sus couriers); si no, los del negocio y, si se pasa la venta, la red de Smarty.
 *
 * ⭐ LA RED DE SMARTY PARA TODOS (20261028150000): una venta propia ve, después de sus transportistas, los couriers de la
 *    red abiertos para su negocio (MERCourier para Vitalex), con el envío que se le cobra o el motivo por el que no.
 *    Si la red no responde, la venta sigue con lo suyo: nunca se queda sin transportistas por eso.
 */
export async function transportistasParaLaVenta(
  tenantId: string,
  destino: LogisticsDestination,
  vaPorSmartyDrop: boolean,
  venta?: { id: string; motorizado: boolean },
): Promise<ResolveCouriersResult> {
  if (!vaPorSmartyDrop) {
    if (!venta) return resolveCouriersForDestination(tenantId, destino);
    const [propios, red] = await Promise.all([
      resolveCouriersForDestination(tenantId, destino),
      opcionesDeLaRed(venta.id).then(laRedComoOpciones).catch(() => [] as AvailableCourierOption[]),
    ]);
    const suyos = new Set(propios.options.map((o) => o.courier_name.toLowerCase()));
    return { ...propios, options: [...propios.options, ...red.filter((o) => !suyos.has(o.courier_name.toLowerCase()))] };
  }
  const agencias = agenciasComoOpciones(await transportistasDeSmartyDrop());
  if (!venta) return agencias;
  const couriers = await couriersComoOpciones(venta.id, venta.motorizado).catch(() => [] as AvailableCourierOption[]);
  return { ...agencias, options: [...couriers, ...agencias.options] };
}

/**
 * La venta que se le pasa a `transportistasParaLaVenta` para que una venta PROPIA traiga la red de Smarty (20261028150000):
 * así Entrega reconoce al courier de la red que ya tiene asignado (su tipo y su logo). En Drop no: sus couriers salen de
 * sus propias opciones.
 */
export function laVentaParaLaRed(
  order: { id: string; is_cod?: boolean | null }, vaPorSmartyDrop: boolean,
): { id: string; motorizado: boolean } | undefined {
  return vaPorSmartyDrop ? undefined : { id: order.id, motorizado: !!order.is_cod };
}

/** Una agencia del directorio de un courier (como la guarda `logistics_courier_agency_locations`). */
export interface AgenciaDelDirectorio {
  id: string; name: string; address: string | null;
  department: string | null; province: string | null; distrito: string | null;
  external_id: string | null; es_agente: boolean | null;
}

/**
 * Las agencias de un courier para una venta de Drop: las del almacén que despacha.
 *
 * 🪤 EL DIRECTORIO DE OLVA VIVE POR CUENTA: lo baja cada negocio con su cuenta de Olva. Un drop no tiene
 *    cuenta de Olva y veía «No hay agencias de Olva cerca». Son las del almacén, que es quien registra
 *    la guía: el mismo nombre de los dos lados, así el almacén la encuentra al registrar.
 */
export async function agenciasDelAlmacen(ventaId: string, courierId: string): Promise<AgenciaDelDirectorio[]> {
  const { data, error } = await supabase.rpc('fn_drop_agencias_del_almacen', { p_venta: ventaId, p_courier: courierId });
  if (error) throw error;
  return (data ?? []) as AgenciaDelDirectorio[];
}

/** La sede de origen de Olva de una venta de Drop, con su detalle del catálogo y el almacén que la eligió. */
export interface SedeDeOrigen {
  headquarter_id: string; name: string | null; department: string | null; province: string | null;
  ubigeo: string | null; ubigeo_id: string | null; address: string | null;
  almacen: string | null;
}

/**
 * La sede de origen de Olva de una venta de Drop (30-oct-2026): la que eligió el almacén que despacha en «Mi almacén»
 * (Lima por defecto). El drop no la elige. `null` si ningún catálogo de la red tiene esa sede.
 */
export async function origenOlvaDeLaVenta(ventaId: string): Promise<SedeDeOrigen | null> {
  const { data, error } = await supabase.rpc('fn_drop_origen_olva_de_la_venta', { p_venta: ventaId });
  if (error) throw error;
  return ((data ?? []) as SedeDeOrigen[])[0] ?? null;
}

/**
 * Los couriers de Smarty Drop por nombre, con su tipo y logo, para la columna Transporte de Ventas.
 *
 * 🪤 SON DE OTRA CUENTA (el courier tiene la suya), así que el catálogo del negocio no los trae y la
 *    columna decía «—» en todo pedido por motorizado (founder, 26-sep). Si la función todavía no existe en la
 *    base (antes de aplicar la migración), no se trae nada y la lista sigue como estaba.
 */
export async function couriersDeSmartyDropPorNombre(): Promise<Map<string, { type: string; logo: string | null }>> {
  const ts = await transportistasDeSmartyDrop().catch(() => [] as TransportistaDeSmartyDrop[]);
  return new Map(ts.map((t) => [t.nombre.toLowerCase(), { type: t.modalidad === 'agencia' ? 'agency' : 'motorizado', logo: t.logo_url || null }]));
}
